import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { API } from '../config/api';
import { CalendarDays, XCircle, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

export default function MesCreneaux() {
  const [data, setData] = useState(null);
  const [modifying, setModifying] = useState(null);
  const [planning, setPlanning] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/client/my-planning`, { withCredentials: true });
      setData(r.data);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm('Confirmez-vous l\'annulation de cette séance ? Votre place sera libérée.')) return;
    try {
      const r = await axios.post(`${API}/client/planning/${id}/cancel`, {}, { withCredentials: true });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const openModify = async (r) => {
    const p = await axios.get(`${API}/planning`);
    setPlanning(p.data.slots.filter((s) => !s.full && s.id !== r.slot_id));
    setModifying(r);
  };

  const modify = async (newSlotId) => {
    try {
      const r = await axios.post(`${API}/client/planning/${modifying.id}/modify`, { new_slot_id: newSlotId }, { withCredentials: true });
      toast.success(r.data.message);
      setModifying(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  if (!data || data.reservations.length === 0) return null;

  return (
    <div className="mb-10 bg-white border border-[#E9DDCC] rounded-2xl p-5" data-testid="mes-creneaux">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays className="w-4 h-4 text-[#C99A55]" />
        <h3 className="font-heading text-lg text-[#493329]">Mes créneaux</h3>
      </div>
      <p className="text-xs text-[#AE9885] mb-3">
        Forfait : {data.active_count}/{data.weekly_limit} séance(s) par semaine{data.pre_opening ? '' : " · Annulation ou modification possible jusqu'à 24h avant la séance · 1 modification autonome par réservation."}
      </p>
      {data.pre_opening && (
        <div className="bg-[#FBF3DC] border border-[#DCC488] rounded-xl p-3 mb-3 text-xs text-[#8B6914] leading-relaxed" data-testid="pre-opening-banner">
          <strong>Planning pré-ouvert :</strong> {data.pre_opening_message}
        </div>
      )}
      {data.reservations.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 border-t border-[#EFE4D0]" data-testid={`creneau-${r.id}`}>
          <div>
            <p className="text-sm text-[#493329]">{r.day} {r.start}–{r.end} <span className="text-xs text-[#7A6455]">{r.type.replace('Pilates Reformer – ', '')}</span>
              <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#EFE4D0] text-[#9A744C]">Pré-réservation</span>
              {r.status === 'waitlist' && <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FBF3DC] text-[#9A744C]">Liste d'attente n°{r.position}</span>}
            </p>
            <p className="text-[11px] text-[#AE9885]">
              {data.pre_opening
                ? 'Séances à partir de la première semaine d\'ouverture officielle'
                : `Prochaine séance : ${new Date(r.next_occurrence).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
              {r.modification_count > 0 ? ' · Modification déjà utilisée' : ''}
            </p>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" disabled={!r.can_modify} onClick={() => openModify(r)}
              className="h-8 text-xs rounded-full border-[#E9DDCC] disabled:opacity-40" data-testid={`modify-${r.id}`}>
              <RefreshCw className="w-3 h-3 mr-1" /> Modifier
            </Button>
            <Button size="sm" variant="outline" disabled={r.status !== 'waitlist' && !r.can_cancel} onClick={() => cancel(r.id)}
              className="h-8 text-xs rounded-full border-[#E9DDCC] text-[#C76542] disabled:opacity-40" data-testid={`cancel-${r.id}`}>
              <XCircle className="w-3 h-3 mr-1" /> Annuler
            </Button>
          </div>
          {(!data.pre_opening && !r.can_cancel && r.status !== 'waitlist') && (
            <p className="w-full text-[11px] text-[#C76542]">Modification et annulation impossibles à moins de 24 h du début de la séance.</p>
          )}
        </div>
      ))}

      <Dialog open={!!modifying} onOpenChange={(o) => !o && setModifying(null)}>
        <DialogContent className="rounded-2xl max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-normal text-[#493329]">Choisir un nouveau créneau</DialogTitle>
            <DialogDescription className="text-xs text-[#7A6455]">Attention : il s'agit de votre unique modification autonome pour cette réservation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {planning.map((s) => (
              <button key={s.id} onClick={() => modify(s.id)}
                className="w-full text-left px-3 py-2.5 rounded-xl border border-[#E9DDCC] hover:border-[#C99A55] text-sm text-[#493329] transition-colors"
                data-testid={`modify-target-${s.id}`}>
                {s.day} {s.start}–{s.end} · {s.type.replace('Pilates Reformer – ', '')} <span className="text-xs text-[#7A6455]">({s.remaining} place(s))</span>
              </button>
            ))}
            {planning.length === 0 && <p className="text-xs text-[#AE9885]">Aucun autre créneau disponible.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
